/**
 * Browser-shared handoff between the self-service email signup and the
 * verify-email screen.
 *
 * Signing up with just an email leaves a pending verify_email flow, and the
 * global AuthChangeRedirector routes every pending verify_email flow to
 * /account/verify-email. These keys let that shared screen know it is part of
 * the self-service signup: where to send the user once verification logs them
 * in (the set-password step), and which address to show.
 *
 * Stored in `localStorage`, NOT `sessionStorage`: the verification email's
 * "Continue signing up" link routinely opens in a NEW TAB, and sessionStorage
 * is per-tab, so the handoff would be invisible there — verification would
 * still authenticate the user, but the redirect would fall back to `/` and
 * skip the set-password step. localStorage is shared across tabs of the same
 * browser (the flow is a same-browser accelerator by design), so the new tab
 * sees the handoff.
 *
 * Because localStorage survives tabs, logout, and abandoned signups, the
 * handoff is bound to a bounded lifetime (HANDOFF_TTL_MS): it is re-stashed on
 * each signup, eagerly cleared on completion or a failed signup
 * (clearEmailVerificationHandoff), and, past its TTL, treated as stale and
 * cleared on read. A stale handoff therefore never outlives its flow and can
 * never route a later, unrelated verify_email flow to the set-password step.
 *
 * All accessors swallow storage failures (private windows, blocked site data):
 * the flow still works — the verify screen falls back to generic copy and the
 * post-login redirect falls back to the app home.
 */

const NEXT_URL_KEY = 'email_verify_next_url';
const ADDRESS_KEY = 'email_verify_address';
const STASHED_AT_KEY = 'email_verify_stashed_at';

/**
 * How long a stashed handoff stays valid. Comfortably longer than a
 * verification code's own lifetime (the code is dead well before this), short
 * enough that an abandoned signup does not linger and misroute a later flow.
 */
const HANDOFF_TTL_MS = 30 * 60 * 1000;

/**
 * True when `path` is a safe in-app destination: a single leading slash, not a
 * protocol-relative `//evil.com` and not an absolute `https://…` URL. Shared by
 * the stored-handoff read and by any raw `?next` consumer so a tampered or
 * crafted value cannot redirect off-site.
 */
export function isSafeInAppPath(
  path: string | null | undefined
): path is string {
  return (
    typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')
  );
}

/**
 * True while a stashed handoff is still within its TTL. A missing or
 * unparseable timestamp, or one older than the window, means it is stale.
 */
function isHandoffFresh(): boolean {
  const stashedAt = Number(localStorage.getItem(STASHED_AT_KEY));
  return (
    Number.isFinite(stashedAt) &&
    stashedAt > 0 &&
    Date.now() - stashedAt < HANDOFF_TTL_MS
  );
}

export function stashEmailVerificationHandoff(
  nextUrl: string,
  email: string
): void {
  try {
    localStorage.setItem(NEXT_URL_KEY, nextUrl);
    localStorage.setItem(ADDRESS_KEY, email);
    localStorage.setItem(STASHED_AT_KEY, String(Date.now()));
  } catch {
    // Storage unavailable — the verify screen falls back to generic copy.
  }
}

export function readAndConsumeEmailVerificationNextUrl(): string | null {
  try {
    if (!isHandoffFresh()) {
      // Stale (abandoned or expired): drop it so it cannot misroute a later
      // verify_email flow to the set-password step.
      clearEmailVerificationHandoff();
      return null;
    }
    const nextUrl = localStorage.getItem(NEXT_URL_KEY);
    // Defence-in-depth against an open redirect: only ever return an in-app
    // absolute path.
    return isSafeInAppPath(nextUrl) ? nextUrl : null;
  } catch {
    return null;
  }
}

export function readAndConsumeEmailVerificationAddress(): string | null {
  try {
    if (!isHandoffFresh()) {
      clearEmailVerificationHandoff();
      return null;
    }
    return localStorage.getItem(ADDRESS_KEY);
  } catch {
    return null;
  }
}

export function clearEmailVerificationHandoff(): void {
  try {
    localStorage.removeItem(NEXT_URL_KEY);
    localStorage.removeItem(ADDRESS_KEY);
    localStorage.removeItem(STASHED_AT_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
